import { useEffect, useMemo, useState } from 'react';

import { LuceraBlotterItem } from '@/model/blotters';
import { createLumeFxRestClient } from '@/services/LumeFxService';
import { useAppSelector } from '@/state/hooks';
import { selectCredentials } from '@/state/reducers/authSlice';
import {
    LumeFxAmendOrderRequest,
    LumeFxOrderStatus,
    LumeFxOrderType,
    LumeFxSide,
    LumeFxTimeInForce
} from '@/types/lumeFx';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { useInstrument } from '@/utils/hooks/useInstrument';
import useOrderBook, { invalidLimitPriceTooltip } from '@/utils/hooks/useOrderBook';
import { useLucera } from '@/utils/providers/LuceraProvider';
import { getAssetName, translatePair } from '@/utils/symbolMapping';
import { yupResolver } from '@hookform/resolvers/yup';
import { BigNumber } from 'bignumber.js';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '../common/Button';
import Divider from '../common/Divider';
import Tooltip from '../common/Tooltip';
import Input from '../inputs/Input';
import InputController from '../inputs/InputController';
import NumberInput from '../inputs/NumberInput';
import NumberInputDisplay from '../inputs/NumberInputDisplay';
import { LuceraAmendOrderFormInput, luceraAmendOrderSchema } from './schema/luceraAmendOrderSchema';

// extends ModalProps
interface LuceraAmendOrderFormProps {
    order: LuceraBlotterItem;
    linkedOrder?: LuceraBlotterItem;
    handlers: any;
    onCancelOrder: (order: LuceraBlotterItem) => void;
}

const defaultValues: Partial<LuceraAmendOrderFormInput> = {
    pair: '',
    side: '',
    ccy: '',
    orderType: '',
    orderId: '',
    status: '',
    duration: '',
    price: 0,
    quantity: 0
};

const ccy2Order = false;
const luceraRestURL = window.config?.lucera?.rest;

const LuceraAmendOrderForm = (props: LuceraAmendOrderFormProps) => {
    const { order, linkedOrder, handlers, onCancelOrder } = props;
    const { orderType, pair, side } = order;

    const { jwt } = useLucera();

    const lumeFxRestClient = createLumeFxRestClient({
        baseUrl: luceraRestURL,
        authorization: jwt
    });

    const credentials = useAppSelector(selectCredentials);

    const { isLimit, isStopMarket, isStopLimit } = useMemo(
        () => ({
            isLimit: orderType === 'Limit',
            isStopMarket: orderType === 'Stop',
            isStopLimit: orderType === 'StopLimit'
        }),
        [orderType]
    );

    const [baseCurrencyCeler, quoteCurrencyCeler] = useMemo(() => (pair || '').split('/'), [pair]);
    const ccyAssetName = useMemo(() => getAssetName(quoteCurrencyCeler), [quoteCurrencyCeler]);

    const { bestAsk, bestBid } = useOrderBook(pair, LumeFxSide[side]);
    const { min_order_size, order_decimals, increments } = useCurrency(baseCurrencyCeler);
    const { pip_size, formatPrice } = useInstrument(pair || '');

    const [locked, setLocked] = useState<boolean>(false);

    const form = useForm<LuceraAmendOrderFormInput>({
        defaultValues,
        mode: 'onChange',
        resolver: yupResolver(
            luceraAmendOrderSchema({
                side,
                orderType,
                bestAsk,
                bestBid,
                formattedBestAsk: formatPrice(bestAsk),
                formattedBestBid: formatPrice(bestBid),
                ccy2Order,
                order_decimals,
                min_order_size,
                currencyPair: pair,
                currencyOut: baseCurrencyCeler
            })
        )
    });

    const {
        reset,
        watch,
        handleSubmit,
        formState: { isDirty, isSubmitting, isValid }
    } = form;

    const onSubmit = async (data: LuceraAmendOrderFormInput, e) => {
        const { quantity, price } = data as LuceraAmendOrderFormInput;
        if (order && credentials) {
            // setLocked(true);
            const payload: LumeFxAmendOrderRequest = {
                Side: LumeFxSide[order.side],
                Symbol: order.pair,
                Account: order.account,
                OrdType: LumeFxOrderType[order.orderType],
                ClOrdID: crypto.randomUUID(),
                OrigClOrdID: order.clientOrderId,
                TimeInForce: LumeFxTimeInForce[order.tif],
                // amended fields
                OrderQty: (quantity || 0).toString()
            };
            if (isLimit) {
                payload['Price'] = (price || 0).toString();
            } else if (isStopMarket) {
                payload['StopPx'] = (price || 0).toString();
            } else if (isStopLimit) {
                payload['StopPx'] = (price || 0).toString();
                payload['Price'] = (price || 0).toString();
            }
            if (linkedOrder) payload['ClOrdLinkID'] = linkedOrder.clOrdLinkId;
            await lumeFxRestClient.amendOrder(payload);
            if (linkedOrder) {
                const linkedPayload: any = {
                    Side: linkedOrder.side,
                    Symbol: linkedOrder.pair,
                    Account: linkedOrder.account,
                    OrdType: linkedOrder.orderType,
                    ClOrdID: crypto.randomUUID(),
                    OrigClOrdID: linkedOrder.clientOrderId,
                    TimeInForce: linkedOrder.tif,
                    // amended fields
                    OrderQty: (quantity || 0).toString()
                };
                await lumeFxRestClient.amendOrder(linkedPayload);
            }

            const isPartOfOcoOrder = order.clOrdLinkId !== undefined;

            setTimeout(() => {
                setLocked(false);
                if (!isPartOfOcoOrder) handlers.close();
            }, 1000);

            if (isPartOfOcoOrder) {
                reset({
                    pair: translatePair(order.pair),
                    side: order.side,
                    ccy: ccyAssetName,
                    orderType: order.orderType,
                    orderId: order.orderId,
                    status: order.status,
                    duration: order.tif,
                    price: +(payload.Price || payload.StopPx || 0),
                    quantity: +(payload.OrderQty || 0)
                });
            }
        }
    };

    const price = watch('price');

    const stopLimitPrice = useMemo(() => {
        const stop = +(price || 0);
        // if (Side[side] === Side.BUY) return BigNumber(stop).plus(slip).toNumber();
        // else if (Side[side] === Side.SELL) return BigNumber(stop).minus(slip).toNumber();
        // else return stop;
        return stop;
    }, [price]);

    const orderPriceType = useMemo(() => (isStopMarket || isStopLimit ? 'Stop Price' : 'Limit Price'), [orderType]);

    const limitPriceTooltip = useMemo(() => {
        if (price && isLimit) {
            if (side === 'BUY') {
                if (ccy2Order && +price < bestBid) {
                    return invalidLimitPriceTooltip(LumeFxSide[side], bestBid, ccy2Order);
                } else if (!ccy2Order && +price > bestAsk) {
                    return invalidLimitPriceTooltip(LumeFxSide[side], bestAsk, ccy2Order);
                }
            }
            if (side === 'SELL') {
                if (ccy2Order && +price > bestAsk) {
                    return invalidLimitPriceTooltip(LumeFxSide[side], bestAsk, ccy2Order);
                } else if (!ccy2Order && +price < bestBid) {
                    return invalidLimitPriceTooltip(LumeFxSide[side], bestBid, ccy2Order);
                }
            }
            return '';
        }
    }, [price, isLimit, side, bestAsk, bestBid, ccy2Order]);

    const amendable = useMemo(() => {
        return [LumeFxOrderStatus.New, LumeFxOrderStatus.PartiallyFilled].includes(LumeFxOrderStatus[order.status]);
    }, [order]);

    useEffect(() => {
        const payload = {
            pair: translatePair(order.pair),
            side: order.side,
            ccy: ccyAssetName,
            orderType: order.orderType,
            orderId: order.orderId,
            status: order.status,
            duration: order.tif,
            price: (isStopMarket || isStopLimit ? order.price : order.price) || 0,
            quantity: order.qty
        };
        reset(payload);
    }, [order]);

    return (
        <FormProvider {...form}>
            <form className="h-full flex flex-col lg:block" onSubmit={handleSubmit(onSubmit)}>
                <div className="flex-1 basis-0 lg:flex-auto p-2 py-3 sm:p-4 h-full lg:h-auto overflow-y-scroll w-full text-brand-text">
                    <div className="space-y-4 overflow-y-auto">
                        <InputController name="pair" label="Symbol Name" disabled>
                            <Input />
                        </InputController>

                        <InputController name="side" label="Side" disabled>
                            <Input />
                        </InputController>

                        <InputController name="ccy" label="Currency" disabled>
                            <Input />
                        </InputController>

                        <InputController name="orderType" label="Order Type" disabled>
                            <Input />
                        </InputController>

                        <InputController name="orderId" label="Order Id" disabled>
                            <Input />
                        </InputController>

                        <InputController name="status" label="Status" disabled>
                            <Input />
                        </InputController>

                        <InputController name="duration" label="Duration" disabled>
                            <Input />
                        </InputController>

                        <Tooltip content={limitPriceTooltip || ''} className="-top-14 right-0 w-2/3">
                            <InputController
                                name="price"
                                label={orderPriceType}
                                className={{
                                    'text-brand-red': limitPriceTooltip,
                                    'text-brand-text': !limitPriceTooltip
                                }}>
                                <NumberInput
                                    currency={ccyAssetName}
                                    placeholder={orderPriceType}
                                    step={BigNumber(1)
                                        .dividedBy(Math.pow(10, pip_size || 0))
                                        .toNumber()}
                                />
                            </InputController>
                        </Tooltip>

                        <InputController name="quantity" label="Quantity">
                            <NumberInput
                                placeholder="Quantity"
                                currency={ccyAssetName}
                                min={min_order_size}
                                step={increments}
                            />
                        </InputController>

                        {isStopLimit && (
                            <InputController label="Stop Limit Price" value={stopLimitPrice}>
                                <NumberInputDisplay placeholder="Stop Limit Price" disabled />
                            </InputController>
                        )}
                    </div>
                </div>
                <Divider />
                <div className="flex flex-row justify-between text-brand-text text-sm items-center gap-2 p-2 sm:p-4">
                    <button
                        type="button"
                        className="rounded-md p-2 px-4 bg-brand-neutral-700 hover:bg-brand-neutral-600"
                        onClick={handlers.close}>
                        Back
                    </button>
                    {amendable && (
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                className="rounded-md p-2 px-4 focus:!ring-brand-red-light !bg-brand-red hover:!bg-brand-red-light w-auto"
                                onClick={() => onCancelOrder(order)}>
                                Cancel Order
                            </Button>
                            <Button
                                type="submit"
                                className="rounded-md p-2 px-4 bg-brand-primary hover:bg-brand-primary-light w-auto"
                                isLoading={isSubmitting || locked}
                                disabled={!isDirty || !isValid || locked}>
                                Save Changes
                            </Button>
                        </div>
                    )}
                </div>
            </form>
        </FormProvider>
    );
};

export default LuceraAmendOrderForm;
